import classNames from 'classnames';
import React, { useId } from 'react';
import Select, { ActionMeta, ClassNamesConfig, Props as SelectProps } from 'react-select';
import { notNil } from '../../core/helpers/is-nil';
import { popoverContainers } from '../../core/popovers/popover-containers';
import { FormHelperText } from '../form-helper-text/form-helper-text';
import { FormInlineNotification, InlineNotification } from '../form-inline-notification/form-inline-notification';
import { FormLabel } from '../form-label/form-label';
import { Spacer } from '../spacer/spacer';
import { DropdownIndicator } from './components/dropdown-indicator';
import { EmotionCacheProvider } from './components/emotion-cache-provider';
import { MultiValueContainer } from './components/multi-value-container';
import { Option } from './components/option';
import { ComboboxOptions, isComboboxOptions } from './types';
import styles from './combobox.module.scss';

type Props = {
  label?: string;
  mandatory?: boolean;
  placeHolderText: string;
  noOptionsFoundText: string;
  options: ComboboxOptions;
  value?: ComboboxOptions;
  onChange?: (newValue: ComboboxOptions, meta: ActionMeta<ComboboxOptions[number]>) => void;
  isMulti?: boolean;
  disabled?: boolean;
  inlineNotification?: InlineNotification;
  helperMsg?: string;
};

const classNamesConfig: ClassNamesConfig<ComboboxOptions[0], boolean> = {
  control: ({ isDisabled, isFocused, menuIsOpen, hasValue }) =>
    classNames(styles.control, styles.fontOverride, {
      [styles.disabled]: isDisabled,
      [styles.focused]: isFocused,
      [styles.menuIsOpen]: menuIsOpen,
      [styles.isDirty]: hasValue, // todo Ben: check if this is a valid implementation when using forms
    }),
  menu: () => styles.menu,
  menuList: () => styles.menuList,
  valueContainer: () => styles.valueContainer,
  indicatorsContainer: () => styles.indicatorsContainer,
  dropdownIndicator: () => styles.dropdownIndicator,
  input: () => classNames(styles.fontOverride, styles.input),
  singleValue: () => styles.fontOverride,
  option: () => styles.option,
  multiValue: () => classNames(styles.multiValueContainer),
  multiValueLabel: () => classNames(styles.fontOverride, styles.multiValue),
  clearIndicator: () => styles.removeElement,
  indicatorSeparator: () => styles.removeElement,
  multiValueRemove: () => styles.removeElement,
  noOptionsMessage: () => classNames(styles.fontOverride, styles.noOptionsMessage),
  placeholder: () => styles.placeholder,
};

export const Combobox: React.FC<Props> = ({
  label,
  mandatory,
  placeHolderText,
  noOptionsFoundText,
  options,
  value: selectedOptions = [],
  onChange,
  isMulti = false,
  disabled = false,
  inlineNotification,
  helperMsg,
}) => {
  const comboboxId = useId();
  const helperId = useId();

  const hasLabel = notNil(label);
  const hasHelper = notNil(helperMsg);
  const hasInlineNotification = notNil(inlineNotification);

  const handleOnChange: SelectProps<ComboboxOptions[number]>['onChange'] = (newValue, meta) => {
    if (isComboboxOptions(newValue)) {
      onChange?.(newValue, meta);
    }
  };

  return (
    <div className={styles.root}>
      {hasLabel ? (
        <FormLabel count={selectedOptions.length} disabled={disabled} htmlFor={comboboxId} mandatory={mandatory}>
          {label}
        </FormLabel>
      ) : null}
      <div className={styles.innerContainer}>
        <EmotionCacheProvider>
          <Select
            aria-describedby={hasHelper ? helperId : undefined}
            aria-label={label}
            classNames={classNamesConfig}
            closeMenuOnSelect={!isMulti}
            components={{ DropdownIndicator, Option, MultiValueContainer }}
            hideSelectedOptions={false}
            id={comboboxId}
            isDisabled={disabled}
            isMulti={isMulti}
            menuPortalTarget={popoverContainers.general()}
            noOptionsMessage={() => noOptionsFoundText}
            options={options}
            placeholder={placeHolderText}
            value={selectedOptions}
            onChange={handleOnChange}
          />
        </EmotionCacheProvider>
        {hasInlineNotification ? (
          <div className={styles.inlineNotifContainer}>
            <FormInlineNotification type={inlineNotification.type}>{inlineNotification.message}</FormInlineNotification>
          </div>
        ) : null}
        {hasHelper ? (
          <>
            <Spacer size="03" />
            <FormHelperText disabled={disabled} id={helperId}>
              {helperMsg}
            </FormHelperText>
          </>
        ) : null}
      </div>
    </div>
  );
};
